<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useAuthStore } from "@/stores/useAuthStore";
import { useMenuStore } from "@/stores/useMenuStore";
import { useRouter, useRoute } from "vue-router";
import { ChevronDown } from "lucide-vue-next";
import Notifikasi from "./Notifikasi.vue";

const authStore = useAuthStore();
const menuStore = useMenuStore();
const router = useRouter();
const route = useRoute();

const isUserReady = computed(() => !!authStore.user);

// Avatar handling
const FILE_BASE = String(import.meta.env.VITE_FILE_BASE_URL || "").replace(
  /\/$/,
  ""
);
const DEFAULT_AVATAR = "/default-avatar.png";
const isAbsoluteUrl = (u) => /^https?:\/\//i.test(u || "");
const resolveAvatar = (file_user) => {
  if (!file_user) return DEFAULT_AVATAR;
  if (isAbsoluteUrl(file_user)) return file_user;
  const path = String(file_user).replace(/^\/+/, "");
  return `${FILE_BASE}/${path}`;
};

const userAvatar = computed(() => resolveAvatar(authStore.user?.file_user));

function fallbackAvatar(e) {
  if (e?.target) e.target.src = DEFAULT_AVATAR;
}

const isUserDropdownOpen = ref(false);
const activeMenu = ref(null);
const openDropdownMenu = ref(null);
const openSubmenu = ref(null);

// NEW: state untuk mobile nav
const isMobileMenuOpen = ref(false);

// Perbaiki computed navMenus dengan guard
const navMenus = computed(() => {
  const menus = menuStore.menus || [];
  console.log("[NavBar] Rendering menus:", menus.length);

  return menus.map((menu) => ({
    ...menu,
    path: formatPath(menu.slug),
    children: Array.isArray(menu.children)
      ? menu.children.map((sub) => ({
          ...sub,
          path: formatPath(sub.slug),
          children: Array.isArray(sub.children)
            ? sub.children.map((child) => ({
                ...child,
                path: formatPath(child.slug),
              }))
            : [],
        }))
      : [],
  }));
});

// Update active menu saat route berubah
const updateActiveMenuFromRoute = (path) => {
  for (const menu of navMenus.value) {
    if (menu.path === path) {
      activeMenu.value = menu.name;
      openDropdownMenu.value = null;
      openSubmenu.value = null;
      return;
    }
    for (const sub of menu.children) {
      if (sub.path === path) {
        activeMenu.value = menu.name;
        openDropdownMenu.value = null;
        openSubmenu.value = null;
        return;
      }
      for (const child of sub.children) {
        if (child.path === path) {
          activeMenu.value = menu.name;
          openDropdownMenu.value = null;
          openSubmenu.value = null;
          return;
        }
      }
    }
  }
};
// Ambil menu dari store saat user sudah login
onMounted(async () => {
  if (authStore.isLoggedIn) {
    await menuStore.fetchMenus();
    updateActiveMenuFromRoute(route.path);
  }
});

// Tutup dropdown & mobile nav saat klik di luar navbar
function handleClickOutside(event) {
  if (
    !event.target.closest(".nav-dropdown") &&
    !event.target.closest(".user-dropdown") &&
    !event.target.closest(".mobile-nav") &&
    !event.target.closest(".mobile-nav-toggle")
  ) {
    openDropdownMenu.value = null;
    openSubmenu.value = null;
    isUserDropdownOpen.value = false;
    isMobileMenuOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

// Toggle dropdown menu navbar (level-1)
const toggleDropdown = (menuName) => {
  openDropdownMenu.value =
    openDropdownMenu.value === menuName ? null : menuName;
  if (openDropdownMenu.value === null) {
    openSubmenu.value = null;
  }
  activeMenu.value = menuName;
};

// Toggle submenu (level-2)
const toggleSubmenu = (id) => {
  openSubmenu.value = openSubmenu.value === id ? null : id;
};

// Toggle dropdown user
const toggleUserDropdown = () => {
  isUserDropdownOpen.value = !isUserDropdownOpen.value;
};

// Set active menu dan tutup dropdown
const setActiveMenu = (menuName) => {
  activeMenu.value = menuName;
  openDropdownMenu.value = null;
  openSubmenu.value = null;
  // untuk mobile: tutup panel saat pilih menu
  isMobileMenuOpen.value = false;
};

// Logout
const logout = async () => {
  await authStore.logout();
  router.push("/login");
};

// Buat slug jadi path router-link
const formatPath = (slug) => {
  if (!slug) return null;
  return slug.startsWith("/") ? slug : `/${slug}`;
};

// Ambil menu dari store
watch(
  () => authStore.isLoggedIn,
  async (isLoggedIn) => {
    if (isLoggedIn) {
      console.log("[NavBar] User logged in, fetching menus");
      await menuStore.fetchMenus();
      updateActiveMenuFromRoute(route.path);
    }
  },
  { immediate: true }
);
watch(
  () => route.path,
  (newPath) => {
    updateActiveMenuFromRoute(newPath);
  },
  { immediate: true }
);



</script>

<template>
  <header class="w-full fixed top-0 left-0 z-[9999999]">
    <!-- Layer Atas -->
    <div
      class="h-[80px] bg-[#2F3547] flex items-center justify-between px-4 md:px-[50px]"
    >
      <!-- Logo -->
      <div class="flex items-center space-x-3">
        <img src="/sipa-43.png" alt="Logo" class="h-10" />
        <img src="/logo-dsda.png" alt="Logo" class="h-10" />
      </div>

      <div class="flex items-center space-x-3">
        <Notifikasi />

        <!-- Tombol toggle mobile nav -->
        <button
          class="mobile-nav-toggle md:hidden px-3 py-2 rounded-md text-white border border-white/40 hover:bg-white/10 transition"
          @click.stop="isMobileMenuOpen = !isMobileMenuOpen"
        >
          <!-- icon hamburger sederhana -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        <!-- User Button -->
        <div v-if="isUserReady" class="relative user-dropdown">
          <button
            @click.stop="toggleUserDropdown"
            class="flex items-center space-x-2 px-4 py-2 bg-[#0B5EC9] text-white rounded-md hover:bg-blue-700 transition"
          >
            <img
              :src="userAvatar"
              alt="Avatar"
              class="w-5 h-5 rounded-full object-cover"
              @error="fallbackAvatar"
            />
            <span class="text-sm font-medium hidden sm:inline">
              {{
                authStore.user?.name?.split(" ")[0] ||
                authStore.user?.username ||
                "User"
              }}
            </span>
          </button>

          <!-- Dropdown User -->
          <div
            v-if="isUserDropdownOpen"
            class="absolute top-12 right-0 w-52 bg-white rounded-lg shadow-lg z-50"
          >
            <ul class="py-2 text-gray-700">
              <li>
                <router-link
                  to="/profile"
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="isUserDropdownOpen = false"
                >
                  Profile Saya
                </router-link>
              </li>
              <li>
                <router-link
                  to="/change-password"
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  @click="isUserDropdownOpen = false"
                >
                  Ubah Password
                </router-link>
              </li>
              <li>
                <button
                  @click="logout"
                  class="w-full text-left font-semibold px-4 py-2 text-red-600 hover:bg-red-100"
                >
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Layer Bawah (Navbar) -->
    <!-- DESKTOP NAV -->
    <div class="hidden md:flex h-[60px] bg-white shadow-md items-center px-4 lg:px-[50px] xl:justify-center">
       <nav class="flex space-x-2 text-gray-700 text-sm font-medium w-full lg:w-auto">
        <div
          v-for="menu in navMenus"
          :key="menu.id"
          class="relative nav-dropdown"
        >
          <!-- Level 1 -->
          <component
            :is="menu.path ? 'router-link' : 'button'"
            :to="menu.path || undefined"
            @click.stop="toggleDropdown(menu.name)"
            class="flex items-center space-x-2 px-3 py-2 rounded-md transition"
            :class="
              activeMenu === menu.name
                ? 'bg-blue-100 text-blue-600'
                : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
            "
          >
            <span v-if="menu.icon" v-html="menu.icon"></span>
            <span>{{ menu.name }}</span>
            <ChevronDown
              v-if="menu.children && menu.children.length"
              class="w-4 h-4"
            />
          </component>

          <!-- Dropdown (Level 2 + 3) -->
          <div
            v-if="menu.children && menu.children.length && openDropdownMenu === menu.name"
            class="absolute top-full mt-1 left-0 w-60 bg-white rounded-md shadow-lg py-2 z-50 border-t-2 border-blue-600"
          >
            <div
              v-for="sub in menu.children"
              :key="sub.id"
              class="relative"
            >
              <button
                v-if="sub.children && sub.children.length"
                @click.stop="toggleSubmenu(sub.id)"
                class="w-full flex items-center justify-between px-4 py-2 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <span>{{ sub.name }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              <!-- <router-link
                v-else-if="sub.path"
                :to="sub.path"
                class="block w-full px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                @click="setActiveMenu(sub.name)"
              >
                {{ sub.name }}
              </router-link> -->
              <router-link
                v-else-if="sub.path"
                :to="sub.path"
                class="block w-full px-4 py-2 transition"
                :class="route.path === sub.path
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'"
                @click="setActiveMenu(menu.name)" 
              >
                {{ sub.name }}
              </router-link>


              <div
                v-else
                class="block w-full px-4 py-2 text-gray-400 bg-gray-100 cursor-default select-none"
              >
                {{ sub.name }}
              </div>

              <!-- Fly-out Level 3 -->
              <div
                v-if="sub.children && sub.children.length && openSubmenu === sub.id"
                class="absolute top-0 left-full w-56 bg-white rounded-md shadow-lg py-2 z-50"
              >
                <!-- <router-link
                  v-for="child in sub.children"
                  :key="child.id"
                  :to="child.path"
                  class="block w-full px-4 py-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition"
                  @click="setActiveMenu(child.name)"
                >
                  {{ child.name }}
                </router-link> -->

                <router-link
                  v-for="child in sub.children"
                  :key="child.id"
                  :to="child.path"
                  class="block w-full px-4 py-2 transition"
                  :class="route.path === child.path
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'"
                  @click="setActiveMenu(menu.name)"
                >
                  {{ child.name }}
                </router-link>

              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>

    <!-- MOBILE NAV -->
    <div
      v-if="isMobileMenuOpen"
      class="mobile-nav md:hidden bg-white shadow-md px-4 py-3"
    >
      <nav class="flex flex-col space-y-1 text-gray-700 text-sm font-medium">
        <div
          v-for="menu in navMenus"
          :key="menu.id"
          class="relative nav-dropdown"
        >
          <!-- Level 1 -->
          <button
            @click.stop="toggleDropdown(menu.name)"
            class="w-full flex items-center justify-between px-3 py-2 rounded-md transition"
            :class="
              activeMenu === menu.name
                ? 'bg-blue-100 text-blue-600'
                : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
            "
          >
            <div class="flex items-center space-x-2">
              <span v-if="menu.icon" v-html="menu.icon"></span>
              <span>{{ menu.name }}</span>
            </div>
            <ChevronDown
              v-if="menu.children && menu.children.length"
              class="w-4 h-4"
            />
          </button>

          <!-- Children level 2 & 3 untuk mobile: tampil vertikal -->
          <div
            v-if="menu.children && menu.children.length && openDropdownMenu === menu.name"
            class="mt-1 ml-4 border-l border-gray-200 pl-2 space-y-1"
          >
            <div v-for="sub in menu.children" :key="sub.id">
              <button
                v-if="sub.children && sub.children.length"
                @click.stop="toggleSubmenu(sub.id)"
                class="w-full flex items-center justify-between px-3 py-2 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition"
              >
                <span>{{ sub.name }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
<!-- 
              <router-link
                v-else-if="sub.path"
                :to="sub.path"
                class="block w-full px-3 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition"
                @click="setActiveMenu(sub.name)"
              >
                {{ sub.name }}
              </router-link> -->
<router-link
  v-else-if="sub.path"
  :to="sub.path"
  class="block w-full px-3 py-2 rounded-md transition"
  :class="route.path === sub.path
    ? 'bg-blue-100 text-blue-600'
    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'"
  @click="setActiveMenu(menu.name)"
>
  {{ sub.name }}
</router-link>

              <div
                v-else
                class="block w-full px-3 py-2 text-gray-400 bg-gray-100 rounded-md cursor-default select-none"
              >
                {{ sub.name }}
              </div>

              <!-- Level 3 -->
              <div
                v-if="sub.children && sub.children.length && openSubmenu === sub.id"
                class="mt-1 ml-4 border-l border-gray-200 pl-2 space-y-1"
              >
                <!-- <router-link
                  v-for="child in sub.children"
                  :key="child.id"
                  :to="child.path"
                  class="block w-full px-3 py-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-md transition"
                  @click="setActiveMenu(child.name)"
                >
                  {{ child.name }}
                </router-link> -->

                <router-link
                  v-for="child in sub.children"
                  :key="child.id"
                  :to="child.path"
                  class="block w-full px-3 py-2 rounded-md transition"
                  :class="route.path === child.path
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'"
                  @click="setActiveMenu(menu.name)"
                >
                  {{ child.name }}
                </router-link>

              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </header>
</template>
